import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {RequestFilterKeys, TableService} from './table.service';
import {TableFilterKeys, tableHeader, tableSubHeader} from '@app/shared/table.constants';
import {DoubleFilterKeys, FilterKeys} from '@app/filter/filter.service';
import {
  ModalRedistributionCreateComponent
} from '@app/modal/modal-redistribution/modal-redistribution-create/modal-redistribution-create.component';
import {DataService} from '@app/shared/data.service';
import {ContextMenuComponent} from '@app/context-menu/context-menu.component';
import {debounceTime, filter, Subject, tap} from 'rxjs';
import {ActivatedRoute} from "@angular/router";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
})
export class TableComponent implements OnInit, AfterViewInit, OnDestroy {
  destroyRef = inject(DestroyRef);
  
  @Input() organizationCode?: string = '';
  @Input() sortKey?: string = '';
  @ViewChild('modalRedistribution')
  modalRedistribution!: ModalRedistributionCreateComponent;
  @ViewChild('contextMenu') contextMenu!: ContextMenuComponent;
  @ViewChild('tableWrapper') tableWrapper!: ElementRef<HTMLDivElement>;
  tableHeader: { label: string; key: string }[] = tableHeader;
  tableSubHeader: { label: string; key: string }[] = tableSubHeader;
  tableErrorCode: number = 0;
  tableBody: { [key: string]: any }[] = [];
  tableBodyErrorCode: number = 0;
  pagination = {
    page: 1,
    perPage: 20,
    totalElements: 0,
    totalPages: 0,
  };
  sort: { [key: string]: 'ASC' | 'DESC' } = {};
  defaultFilters: { [key: string]: any } = {};
  selectedFilters: { [key: string]: any } = {};
  filterKeys: any = TableFilterKeys;
  requestFilterKeys: any = RequestFilterKeys;
  resetFilters: boolean = false;
  doubleFilterKeys: any = DoubleFilterKeys;
  activeRow: any;
  hoveredRow: any;
  isModalRedistributionVisible: boolean = false;
  userOrganizations: Array<string> = [];
  isExpired: boolean = false;
  isShownOnlyMyOrganisations: boolean = false;
  userOrganizationCodes: Array<string> = [];
  openRows: number[] = [];
  filtersAmount: number = 0
  
  $requestPayload = new Subject<any>();
  
  isLoading: boolean = false;

  constructor(
    private tableService: TableService,
    private dataService: DataService,
    private activatedRoute: ActivatedRoute,
  ) {
  }

  ngOnInit(): void {
    this.subscribeToRequestPayload();
    
    this.dataService.breadcrumbsArray.next([...this.activatedRoute.snapshot.data['breadcrumbs']])
    
    this.defaultFilters = {
      ...(this.organizationCode && {
        organizationCode: [this.organizationCode],
      }),
    };

    this.dataService.userOrganizationsValue.subscribe((organizations) => {
      organizations.forEach((organization) => {
        this.userOrganizations.push(organization['code'].toString());
        this.userOrganizations.push(organization['id'].toString());
        this.userOrganizationCodes.push(organization['code'].toString());
      });
      
      this.$requestPayload.next({
        pageable: {
          page: 0,
          size: this.pagination.perPage,
        },
        filter: {...this.defaultFilters, ...this.selectedFilters},
      });
    });


    this.dataService.isExpiredValue
      .pipe(filter((isExpired) => this.isExpired !== isExpired))
      .subscribe((isExpired) => {
        if (isExpired) this.selectedFilters['isExpired'] = isExpired;
        else delete this.selectedFilters['isExpired'];
        this.isExpired = isExpired;
        this.$requestPayload.next({
          pageable: {
            page: 0,
            size: this.pagination.perPage,
          },
          filter: {...this.defaultFilters, ...this.selectedFilters},
        });
      });

    this.dataService.showMyOrganizationValue
      .pipe(filter((showMyOrganizations) => this.isShownOnlyMyOrganisations !== showMyOrganizations))
      .subscribe((showMyOrganizations) => {
        this.isShownOnlyMyOrganisations = showMyOrganizations;
        this.$requestPayload.next({
          pageable: {
            page: 0,
            size: this.pagination.perPage,
          },
          filter: {...this.defaultFilters, ...this.selectedFilters},
        });
      })
  }

  ngAfterViewInit() {
    this.contextMenu.hidden.subscribe(() => {
      if (
        (this.activeRow || this.hoveredRow) &&
        !this.isModalRedistributionVisible
      ) {
        this.activeRow = null;
        this.hoveredRow = null;
      }
    });

    this.modalRedistribution.visible.subscribe(() => {
      this.isModalRedistributionVisible = true;
    });

    this.modalRedistribution.hidden.subscribe(() => {
      if (this.activeRow || this.hoveredRow) {
        this.isModalRedistributionVisible = false;
        this.activeRow = null;
        this.hoveredRow = null;
      }
    });
  }
  
  ngOnDestroy() {
    this.$requestPayload.complete();
  }
  
  getBodyData(payload?: any): void {
    this.isLoading = true;
    let data = window.structuredClone(payload);
    
    if (this.isShownOnlyMyOrganisations && this.userOrganizationCodes &&  !data.filter.organizationCode) {
      data.filter.organizationCode = this.userOrganizationCodes;
    }

    
    this.tableService.getBodyData(data).subscribe(
      (bodyData) => {
        this.isLoading = false;
        this.openRows = [];
        this.tableBody = bodyData.content;
        this.pagination.totalPages = bodyData.totalPages;
        this.pagination.totalElements = bodyData.totalElements;
        this.tableWrapper.nativeElement.scrollTop = 0;
        if (this.tableBody.length === 0) this.tableBodyErrorCode = 404;
        else this.tableBodyErrorCode = 0;
      },
      (error) => {
        this.isLoading = false;
        if (error.status === 500) this.tableErrorCode = error.status;
        else this.tableBodyErrorCode = error.status;
        console.error('Error fetching table body data', error);
      }
    );
  }

  getPreselectedOption(key: string): { [key: string]: any } {
    switch (key) {
      case 'organizationCode':
        return {[this.filterKeys[key]]: this.organizationCode};
      default:
        return {};
    }
  }

  getCellTitle(value: string): string {
    return value && value.length > 76 ? value : '';
  }

  isColumnFilterable(columnKey: string): boolean {
    return Object.keys(this.filterKeys).includes(columnKey);
  }

  onFilterChange({key, payload}: any): void {
    if (
      !payload ||
      ((Array.isArray(payload) || typeof payload === 'string') &&
        !payload.length) ||
      (this.doubleFilterKeys.includes(key) && !payload.from && !payload.to)
    ) {
      delete this.selectedFilters[this.requestFilterKeys[key]];
      this.$requestPayload.next({
        pageable: {
          page: 0,
          size: this.pagination.perPage,
        },
        filter: {...this.defaultFilters, ...this.selectedFilters},
      });
      return;
    }
    
    this.selectedFilters[this.requestFilterKeys[key]] = payload;
    this.$requestPayload.next({
      pageable: {
        page: 0,
        size: this.pagination.perPage,
      },
      filter: {...this.defaultFilters, ...this.selectedFilters},
    })
  }

  onSortChange(payload: { [key: string]: 'ASC' | 'DESC' }): void {
    this.$requestPayload.next({
      pageable: {
        page: 0,
        size: this.pagination.perPage,
      },
      filter: {...this.defaultFilters, ...this.selectedFilters},
      sort: {...this.sort, ...payload},
    });
    this.sort = {...this.sort, ...payload};
  }

  onResetFilters(): void {
    this.resetFilters = true;
    setTimeout(() => {
      this.resetFilters = false;
    }, 0);
    this.dataService.showMyOrganization = false;
    this.isShownOnlyMyOrganisations = false;
    this.selectedFilters = {};
    this.$requestPayload.next({
      pageable: {
        page: 0,
        size: this.pagination.perPage,
      },
      filter: this.defaultFilters,
    });
  }

  onFilterError(error: any): void {
    console.error(error.text, error.payload);
    this.tableErrorCode = error.payload.status;
  }

  onPageChange(newPage: number): void {
    this.$requestPayload.next({
      filter: {...this.defaultFilters, ...this.selectedFilters},
      pageable: {
        page: newPage - 1,
        size: this.pagination.perPage,
      },
    });
  }

  onRowClick(event: MouseEvent, row: any): void {
    if (this.userOrganizations.includes(row.organizationId.toString())) {
      this.activeRow = row;
      this.contextMenu.show({
        coordinates: {x: event.clientX, y: event.clientY},
        options: [
          {
            label: 'Запропонувати на перерозподіл',
            action: () => this.modalRedistribution.open(row),
            isAvailable: this.activeRow.availableQuantity > 0 && !(this.activeRow.subtype === 'TEMPLATE' || this.activeRow.subtype === 'COMPONENT'),
          },
        ],
      });
    }
  }

  onRowHover(row: any): void {
    if (this.userOrganizations.includes(row.organizationId.toString()))
      this.hoveredRow = row;
  }

  onTableScroll(): void {
    if (this.contextMenu) {
      this.contextMenu.hide();
    }
  }
  
  onToggleRow(index: number) {
    if (!this.openRows.includes(index)) {
      this.openRows.push(index);
    } else {
      this.openRows.splice(this.openRows.indexOf(index), 1);
    }
  }
  
  isOpen(index: number) {
    return  this.openRows.includes(index);
  }
  
  subscribeToRequestPayload() {
    this.$requestPayload
        .pipe(
            takeUntilDestroyed(this.destroyRef),
            debounceTime(400),
            tap(() => {
              this.filtersAmount = Object.keys(this.selectedFilters).length + Number(this.isShownOnlyMyOrganisations);
            })
        )
        .subscribe((payload) => {
          this.getBodyData(payload);
        });
  }
}
